import React from 'react';
import {
  Box,
  Button,
  Field,
  Flex,
  Heading,
  Icon,
  IconButton,
  Input,
  Link,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import { Helmet } from 'react-helmet-async';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@kuow/shared-ui';
import { createRecoveryRequest, logger } from '@kuow/core';

const DEFAULT_REDIRECT = '/members/profile';

interface LocationState {
  from?: string;
}

type SignInMode = 'password' | 'magic-link' | 'reset-password';

const SUBMIT_LABEL: Record<SignInMode, string> = {
  password: 'Submit',
  'magic-link': 'Send Magic Link',
  'reset-password': 'Start Recovery',
};

export const SignInPage: React.FC = () => {
  const { isAuthenticated, initialized, login, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = (location.state as LocationState | null)?.from;
  const redirectTo =
    fromState && fromState.startsWith('/') && !fromState.startsWith('//')
      ? fromState
      : DEFAULT_REDIRECT;

  const [mode, setMode] = React.useState<SignInMode>('magic-link');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [errors, setErrors] = React.useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = React.useState<string | null>(null);
  const [recoveryStarted, setRecoveryStarted] = React.useState<string | null>(null);
  const [recoveryLoading, setRecoveryLoading] = React.useState(false);

  if (initialized && isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const switchMode = (next: SignInMode) => {
    setMode(next);
    setErrors({});
    setFormError(null);
    setMagicLinkSent(null);
    setRecoveryStarted(null);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const nextErrors: { email?: string; password?: string } = {};
    if (!email.trim()) nextErrors.email = 'Email Address is required';
    if (mode === 'password' && !password.trim()) {
      nextErrors.password = 'Password is required';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setFormError(null);

    if (mode === 'magic-link' || mode === 'reset-password') {
      const recoveryMethod = mode === 'magic-link' ? 'magic' : 'reset';
      const trimmedEmail = email.trim();
      setRecoveryLoading(true);
      void (async () => {
        try {
          await createRecoveryRequest(trimmedEmail, recoveryMethod);
          if (recoveryMethod === 'magic') {
            setMagicLinkSent(trimmedEmail);
          } else {
            setRecoveryStarted(trimmedEmail);
          }
        } catch (error) {
          logger.error('Recovery request failed', { error, recoveryMethod });
          setFormError('Unable to send email. Please try again.');
        } finally {
          setRecoveryLoading(false);
        }
      })();
      return;
    }

    void (async () => {
      const success = await login(email.trim(), password, true);
      if (success) {
        navigate(redirectTo, { replace: true });
        return;
      }
      setFormError('Invalid email or password. Please try again.');
    })();
  };

  const showPasswordField = mode === 'password';
  const toggleLabel =
    mode === 'password'
      ? 'sign in with magic link »'
      : mode === 'magic-link'
        ? 'sign in with password »'
        : 'sign in »';
  const toggleTarget: SignInMode = mode === 'password' ? 'magic-link' : 'password';

  return (
    <>
      <Helmet>
        <title>Sign In | KUOW</title>
      </Helmet>
      <Box w="full" py={{ base: 10, md: 16 }} display="flex" flex="1" alignItems="center">
        <Box w="full" maxW={{ base: 'full', md: '450px' }} mx="auto" px={{ base: 4, md: 6 }}>
          <Box
            bg="bg"
            borderRadius="l3"
            boxShadow={{ base: 'none', md: 'lg' }}
            p={{ base: 6, md: 6 }}
          >
            <Box asChild>
              <form onSubmit={handleSubmit} noValidate>
                <VStack gap={4} align="stretch">
                  <Heading fontSize="2xl" fontWeight="semibold" lineHeight="32px">
                    Sign In
                  </Heading>

                  <Text fontSize="xs" lineHeight="16px">
                    Enter your email address below in order to sign in and see your previous donations,
                    thank you gifts, and manage your monthly giving.
                  </Text>

                  <Stack gap={3.5} align="stretch">
                    <Field.Root invalid={Boolean(errors.email)} required>
                      <Field.Label>
                        Email Address
                        <Field.RequiredIndicator />
                      </Field.Label>
                      <Input
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                      />
                      {errors.email ? <Field.ErrorText>{errors.email}</Field.ErrorText> : null}
                    </Field.Root>

                    {showPasswordField ? (
                      <Box>
                        <Field.Root invalid={Boolean(errors.password)} required>
                          <Field.Label>
                            Password
                            <Field.RequiredIndicator />
                          </Field.Label>
                          <Box position="relative" w="full">
                            <Input
                              type={showPassword ? 'text' : 'password'}
                              autoComplete="current-password"
                              value={password}
                              onChange={(event) => setPassword(event.target.value)}
                              pr="10"
                            />
                            <IconButton
                              variant="ghost"
                              size="sm"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                              onClick={() => setShowPassword((prev) => !prev)}
                              position="absolute"
                              right="1"
                              top="50%"
                              transform="translateY(-50%)"
                              disabled={loading}
                              zIndex={1}
                            >
                              <Icon as={showPassword ? LuEyeOff : LuEye} />
                            </IconButton>
                          </Box>
                          {errors.password ? (
                            <Field.ErrorText>{errors.password}</Field.ErrorText>
                          ) : null}
                        </Field.Root>
                        <Flex justify="flex-end" mt="1">
                          <Link
                            asChild
                            fontSize="xs"
                            color="river.solid"
                            textDecoration="underline"
                            fontWeight="normal"
                          >
                            <button type="button" onClick={() => switchMode('reset-password')}>
                              Forgot your password?
                            </button>
                          </Link>
                        </Flex>
                      </Box>
                    ) : null}

                    {formError ? (
                      <Box
                        bg="red.subtle"
                        border="1px solid"
                        borderColor="red.emphasized"
                        color="red.fg"
                        borderRadius="l2"
                        px="4"
                        py="3"
                      >
                        <Text fontSize="sm" textAlign="center">
                          {formError}
                        </Text>
                      </Box>
                    ) : null}

                    {mode === 'magic-link' && magicLinkSent ? (
                      <Box
                        bg="green.subtle"
                        border="1px solid"
                        borderColor="green.emphasized"
                        color="green.fg"
                        borderRadius="l2"
                        px="4"
                        py="3"
                      >
                        <Text fontSize="sm" textAlign="center">
                          If an account exists for {magicLinkSent}, a magic link is on its way.
                        </Text>
                      </Box>
                    ) : null}

                    {mode === 'reset-password' && recoveryStarted ? (
                      <Box
                        bg="green.subtle"
                        border="1px solid"
                        borderColor="green.emphasized"
                        color="green.fg"
                        borderRadius="l2"
                        px="4"
                        py="3"
                      >
                        <Text fontSize="sm" textAlign="center">
                          If an account exists for {recoveryStarted}, recovery instructions are on
                          their way.
                        </Text>
                      </Box>
                    ) : null}

                    <Box display="flex" justifyContent="center">
                      <Button
                        type="submit"
                        variant="solid"
                        colorPalette="river"
                        loading={loading || recoveryLoading}
                      >
                        {SUBMIT_LABEL[mode]}
                      </Button>
                    </Box>
                  </Stack>

                  <Text fontSize="xs" textAlign="center">
                    Or,{' '}
                    <Link
                      asChild
                      color="river.solid"
                      textDecoration="underline"
                      fontWeight="normal"
                    >
                      <button type="button" onClick={() => switchMode(toggleTarget)}>
                        {toggleLabel}
                      </button>
                    </Link>
                  </Text>
                </VStack>
              </form>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};
